import {
    ChangeDetectionStrategy,
    Component,
    computed,
    effect,
    ElementRef,
    inject,
    Injector,
    input,
    InputSignal,
    OnInit,
    output,
    OutputEmitterRef,
    signal,
    Signal,
    viewChild,
    WritableSignal
} from "@angular/core";
import {ResponsiveDirective} from "../../responsive/responsive.directive";
import {AnalyticsService} from '../../logging/analytics.service';
import {AppDealActionStyle} from "../../configuration/app-entity.model";
import {NavigationService} from "../../navigation/navigation.service";
import {IdentificationMode} from '../../identity/identification-page/identification-mode';
import {IdentityModel} from '../../identity/identity.model';
import {AppPromoData} from '../../identity/member-summary-entity.model';
import {TouchableDirective} from "../../ui-elements/touchable.directive";
import {Direction} from '../../ui-elements/direction';
import {IconButtonComponent} from "../../buttons/icon-button/icon-button.component";
import {IconButtonType} from "../../buttons/icon-button/icon-button";
import {LinkComponent} from "../../buttons/link/link.component";
import {DealData} from '../../deal/deal-entity.model';
import {MenuItemData} from '../../menu/menu-entity.model';
import {NgClass} from '@angular/common';
import {TranslateModule} from '@ngx-translate/core';
import {PromoCardComponent} from "../promo-card/promo-card.component";
import {SwipableDirective} from '../../ui-elements/swipable.directive';
import {CssVarDirective} from '../../utils/css-var.directive';
import {ElementWidthDirective} from '../../utils/element-width.directive';
import {whenSignalMatches} from '../../utils/signal-utils';
import {ConfigurationModel} from '../../configuration/configuration.model';
import {PromoEvent} from '../promo.model';
import {ActivatableDirective} from '../../ui-elements/activatable.directive';

enum SlideType {
    Login,
    Registration,
    Deal,
    Promo,
    More
}

enum ScrollType {
    Auto,
    Swipe,
    Click
}

class PromoSlide {
    public readonly deal: DealData;
    public readonly promo: AppPromoData;
    public readonly slideId: string;

    constructor(public type: SlideType,
                public data?: DealData | AppPromoData) {
        this.deal = this.type === SlideType.Deal ? <DealData>this.data : undefined;
        this.promo = this.type === SlideType.Promo ? <AppPromoData>this.data : undefined;
        this.slideId = this.type === SlideType.Deal ? this.deal.id
            : this.type === SlideType.Promo ? this.promo.id
                : this.type === SlideType.Login ? "LOGIN"
                    : this.type === SlideType.Registration ? "REGISTRATION"
                        : this.type === SlideType.More ? "MORE"
                            : undefined;
    }
}

@Component({
    selector: "woa-promo-carousel",
    templateUrl: "./promo-carousel.component.html",
    styleUrl: "./promo-carousel.component.scss",
    imports: [
        CssVarDirective,
        ElementWidthDirective,
        IconButtonComponent,
        LinkComponent,
        NgClass,
        PromoCardComponent,
        ResponsiveDirective,
        SwipableDirective,
        TouchableDirective,
        TranslateModule,
        ActivatableDirective
    ],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class PromoCarouselComponent implements OnInit {
    private injector: Injector = inject(Injector);
    public configurationModel: ConfigurationModel = inject(ConfigurationModel);
    public identityModel: IdentityModel = inject(IdentityModel);
    private analyticsService: AnalyticsService = inject(AnalyticsService);
    private navigationService: NavigationService = inject(NavigationService);
    private element: ElementRef = inject(ElementRef);

    SlideType = SlideType;
    ScrollType = ScrollType;
    Direction = Direction;
    IconButtonType = IconButtonType;
    AppDealActionStyle = AppDealActionStyle;

    private static readonly AUTO_SCROLL_MILLIS: number = 5000;

    public readonly nrVisibleSlides: InputSignal<number> = input.required<number>();

    public readonly morePromotionalCards: InputSignal<boolean> = input<boolean>(false);
    public readonly showMorePromotionalCardsAsSlide: InputSignal<boolean> = input<boolean>(true);
    public readonly promoteMemberLogin: InputSignal<boolean> = input<boolean>(false);
    public readonly promoteMemberRegistration: InputSignal<boolean> = input<boolean>(false);
    public readonly deals: InputSignal<DealData[]> = input<DealData[]>([]);
    public readonly promos: InputSignal<AppPromoData[]> = input<AppPromoData[]>([]);
    public readonly autoScrollEnabled: InputSignal<boolean> = input<boolean>(true);

    public onOrderDeal: OutputEmitterRef<DealData> = output<DealData>();
    public onPromoActioned: OutputEmitterRef<PromoEvent> = output<PromoEvent>();

    public readonly touchable: Signal<TouchableDirective> = viewChild(TouchableDirective);

    private baseSlides: WritableSignal<PromoSlide[]> = signal([]);
    public scrolledBack: WritableSignal<boolean> = signal<boolean>(false);
    public scrolledForth: WritableSignal<boolean> = signal<boolean>(false);
    public scrolling: WritableSignal<ScrollType | undefined> = signal<ScrollType | undefined>(undefined);
    public ignoreSwipe: WritableSignal<boolean> = signal<boolean>(false);

    public scrollable: Signal<boolean> = computed<boolean>(() => this.nrVisibleSlides() < this.baseSlides().length);
    public swipeMaxDistance: Signal<number> = computed<number>(() => this.element.nativeElement.clientWidth / this.nrVisibleSlides());
    public swipeRangeProportionOfSize: Signal<number> = computed<number>(() => 1 / this.nrVisibleSlides());

    // If scrollable, add two "dummy" slides to each end.
    // One slide on each end will be partially visible, and the next slide on each end will become partially visible after scrolling in that direction.
    public visibleSlides: Signal<PromoSlide[]> = computed(() => [
        ...(this.scrollable() ? [this.baseSlides()[this.baseSlides().length - 2], this.baseSlides()[this.baseSlides().length - 1]] : []),
        ...this.baseSlides().slice(0, this.nrVisibleSlides()),
        ...(this.scrollable() ? [this.baseSlides()[this.nrVisibleSlides() % this.baseSlides().length], this.baseSlides()[(this.nrVisibleSlides() + 1) % this.baseSlides().length]] : [])
    ]);

    private autoScrollTimer: ReturnType<typeof setInterval>;
    private autoScrollBlocked: boolean = false;

    constructor() {
        effect(() => {
            this.baseSlides.set([
                ...(this.promoteMemberLogin() && this.configurationModel.app() && this.identityModel.promoteMemberLogin() ? [new PromoSlide(SlideType.Login)] : []),
                ...(this.promoteMemberRegistration() && this.configurationModel.app() && this.identityModel.promoteMemberLogin() ? [new PromoSlide(SlideType.Registration)] : []),
                ...this.deals().map((deal: DealData) => new PromoSlide(SlideType.Deal, deal)),
                ...this.promos().map((promo: AppPromoData) => new PromoSlide(SlideType.Promo, promo)),
                ...(this.morePromotionalCards() && this.showMorePromotionalCardsAsSlide() ? [new PromoSlide(SlideType.More)] : [])
            ]);
            this.initiateAutoScroll();
        });
    }

    ngOnInit(): void {
        this.scrolledBack.set(false);
        this.scrolledForth.set(false);
        this.scrolling.set(undefined);

        whenSignalMatches(this.touchable, (touchable: TouchableDirective) => !!touchable, {injector: this.injector}).then(() => effect(() => {
            if (this.touchable().touched()) {
                this.autoScrollBlocked = false;
                this.terminateAutoScroll();
            } else if (!this.autoScrollBlocked) {
                this.initiateAutoScroll();
            }
        }, {injector: this.injector}));
    }

    private initiateAutoScroll(): void {
        this.terminateAutoScroll();
        if (this.autoScrollEnabled() && this.scrollable()) {
            this.autoScrollTimer = setTimeout(() => this.scroll(true, ScrollType.Auto), PromoCarouselComponent.AUTO_SCROLL_MILLIS);
        }
    }

    private terminateAutoScroll(): void {
        if (this.autoScrollTimer) {
            clearTimeout(this.autoScrollTimer);
            this.autoScrollTimer = undefined;
        }
    }

    public blockAutoScroll(): void {
        this.autoScrollBlocked = true;
        this.terminateAutoScroll();
    }

    public scroll(forth: boolean, scrollType: ScrollType): void {
        if (scrollType === ScrollType.Click) {
            this.ignoreSwipe.set(true);
        }

        if (forth && scrollType === ScrollType.Swipe && this.scrolling() === ScrollType.Auto) {
            this.scrolling.set(ScrollType.Swipe);
            return;
        }

        this.initiateAutoScroll();

        // Shuffle a slide from one end to the other.
        this.baseSlides.set(forth
            ? [...this.baseSlides().slice(1), this.baseSlides().at(0)]
            : [this.baseSlides().at(-1), ...this.baseSlides().slice(0, -1)]);

        // Immediately compensate for the shuffled slide by moving the slider window without a transition to the current first slide
        this.scrolledBack.set(!forth);
        this.scrolledForth.set(forth);
        this.scrolling.set(undefined);

        // Once the above has rendered, turn on the transition and move the window back to the new first slide.
        setTimeout(() => {
            this.scrolling.set(scrollType);
            this.scrolledBack.set(false);
            this.scrolledForth.set(false);
            setTimeout(() => {
                this.ignoreSwipe.set(false);
                this.scrolling.set(undefined);
            }, scrollType === ScrollType.Auto ? 1500 : scrollType === ScrollType.Swipe ? 250 : 500);
        });
    }

    public promoActioned(promo: AppPromoData, menuItem: MenuItemData): void {
        this.onPromoActioned.emit(new PromoEvent(promo, menuItem));
    }

    async login(): Promise<void> {
        return this.navigationService.createPage("woa-identification-page", {"mode": IdentificationMode.LOGIN});
    }

    async register(): Promise<void> {
        return this.navigationService.createPage("woa-identification-page", {
            "mode": IdentificationMode.REGISTER,
            "defaultRegistrationFromOrderingContext": true
        });
    }

    public async showAllDeals(): Promise<void> {
        void this.analyticsService.trackNavOption("deals");
        return this.navigationService.createPage("woa-deals-page");
    }
}
